import { useSearchParams } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import useTheme from 'chat-ui/src/hooks/useTheme';
import { ERROR_MESSAGES } from '@common/constants';
import Image from 'chat-ui/src/components/Image/Image';
import Illustration from '@/assets/ConversationNotFound.svg';
import TelletLogo from '@/assets/tellet-logo-main.svg';
import EmpathyLogo from '@/assets/empathy-logo-black.svg';
import { ECompanyThemeName } from '@/enums/enums';
import { TRANSLATIONS } from '@/constants/translations';

const ERROR_STATUSES = {
  PROJECT_DO_NOT_EXIST: '404',
  NETWORK_ERROR: '500',
  MAXIMUM_COUNT_OF_CONVERSATION_EXCEEDED: '409',
  FORBIDDEN: '403',
};

const themeLogosList = {
  [ECompanyThemeName.DEFAULT]: (
    <Image
      src={TelletLogo}
      alt="logo"
      height={50}
      width={50}
      className="absolute w-32 h-fit top-5 left-5"
    />
  ),
  [ECompanyThemeName.EMPATHY]: (
    <Image
      src={EmpathyLogo}
      alt="logo"
      height={120}
      width={120}
      className="absolute w-44 h-fit top-5 left-5"
    />
  ),
};

function ChatRedirectedErrorPage() {
  const { themeName, currentTheme } = useTheme();
  const [searchParams] = useSearchParams();
  const errorStatus = searchParams.get('error_status') as string;
  const quotaFullLink = searchParams.get('quota_full_link');
  const errorMessage = searchParams.get('error-message');
  const isErrorStatusValid = Object.values(ERROR_STATUSES).includes(errorStatus);

  const [countdown, setCountdown] = useState(5);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const isMaximumCountOfConversationsExceeded =
    errorStatus === ERROR_STATUSES.MAXIMUM_COUNT_OF_CONVERSATION_EXCEEDED;
  const projectDoNotExist =
    errorStatus === ERROR_STATUSES.PROJECT_DO_NOT_EXIST ||
    errorStatus === ERROR_STATUSES.NETWORK_ERROR;
  const isEmpathyTheme = themeName === ECompanyThemeName.EMPATHY;
  const isForbidden = errorStatus === ERROR_STATUSES.FORBIDDEN;
  const isProjectCompleted = isForbidden && errorMessage === ERROR_MESSAGES.PROJECT_IS_COMPLETED;
  const isProjectInDraftOrReadyStatus =
    isForbidden && errorMessage === ERROR_MESSAGES.PROJECT_NOT_PUBLISHED;

  useEffect(() => {
    /**
     * Auto-start redirect countdown when quota is exceeded and redirect URL exists
     */
    if (isMaximumCountOfConversationsExceeded && quotaFullLink) {
      setIsRedirecting(true);
    }
  }, [isMaximumCountOfConversationsExceeded, quotaFullLink]);

  useEffect(() => {
    /**
     * Handle countdown timer and redirect logic
     * - Decrements countdown every second when redirecting
     * - Redirects when countdown reaches 0
     */
    if (isRedirecting && countdown > 0) {
      intervalRef.current = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    } else if (isRedirecting && countdown === 0 && quotaFullLink) {
      window.location.href = quotaFullLink;
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isRedirecting, countdown, quotaFullLink]);

  const handleCancelRedirect = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    setIsRedirecting(false);
    setCountdown(5);
  };

  const handleGoNow = () => {
    if (quotaFullLink) {
      window.location.href = quotaFullLink;
    }
  };

  return (
    <div className="relative flex items-center justify-center min-h-screen px-4 mx-auto text-center">
      {/* logo */}
      {themeLogosList[themeName]}

      <div className="flex flex-col items-center justify-center gap-2 mx-auto text-center">
        <img src={Illustration} alt="illustration" className="min500:w-full w-[500px]" />
        <h1 className="text-5xl font-bold mt-14 text-dark min500:text-4xl">
          {projectDoNotExist && TRANSLATIONS.errors.projectDoesNotExist.title(isEmpathyTheme)}
          {isMaximumCountOfConversationsExceeded && TRANSLATIONS.errors.maximumParticipants.title}
          {isProjectCompleted && TRANSLATIONS.errors.projectCompleted.title}
          {isProjectInDraftOrReadyStatus && TRANSLATIONS.errors.projectInDraftOrReadyStatus.title}
          {!isErrorStatusValid && TRANSLATIONS.errors.somethingMissing.title}
        </h1>
        <p className="mt-2 text-lg font-medium text-light-dark">
          {projectDoNotExist && TRANSLATIONS.errors.projectDoesNotExist.message}
          {isMaximumCountOfConversationsExceeded && TRANSLATIONS.errors.maximumParticipants.message}
          {isProjectCompleted && TRANSLATIONS.errors.projectCompleted.message}
          {isProjectInDraftOrReadyStatus && TRANSLATIONS.errors.projectInDraftOrReadyStatus.message}
          {!isErrorStatusValid && TRANSLATIONS.errors.somethingMissing.message}
        </p>

        {/* Quota full redirect section */}
        {isMaximumCountOfConversationsExceeded && quotaFullLink && (
          <div className="mt-6 w-full max-w-md">
            {isRedirecting && (
              <div
                className="mb-4 p-4 rounded-lg border"
                style={{
                  backgroundColor: `${currentTheme?.primary || '#10b981'}15`,
                  borderColor: `${currentTheme?.primary || '#10b981'}40`,
                }}
              >
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm" style={{ color: currentTheme?.primary || '#10b981' }}>
                    {TRANSLATIONS.redirect.redirectingIn}{' '}
                    <span className="font-bold">{countdown}</span> {TRANSLATIONS.redirect.seconds}
                  </p>
                  <button
                    onClick={handleCancelRedirect}
                    className="text-sm underline transition-opacity hover:opacity-80"
                    style={{ color: currentTheme?.primary || '#10b981' }}
                    type="button"
                  >
                    {TRANSLATIONS.redirect.cancel}
                  </button>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="h-2 rounded-full transition-all duration-1000"
                    style={{
                      backgroundColor: currentTheme?.primary || '#10b981',
                      width: `${((5 - countdown) / 5) * 100}%`,
                    }}
                  />
                </div>
              </div>
            )}
            <button
              onClick={handleGoNow}
              className="w-full py-3 px-6 text-white font-semibold rounded-lg transition-opacity hover:opacity-90"
              style={{ backgroundColor: currentTheme?.primary || '#10b981' }}
              type="button"
            >
              {TRANSLATIONS.redirect.returnToPanel}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default ChatRedirectedErrorPage;
